import { Messages } from '../../types';
import { getLocaleMessages } from '../../i18n/locale-messages';
import { LOCALIZED_PATHS, BASE_URL } from '../constants/url-constants';
import { buildClampedTitle } from '../seo/clamp-meta-title';

export function generateSeoTitle(
  location: string[],
  remoteness: string[],
  roleLevel: string[],
  languages: string[],
  industry: string[],
  company: string[],
  locale: string,
): string {
  // Join each array into a comma-separated string if it has values
  const locStr = location.length ? location.join(', ') : '';
  const remStr = remoteness.length ? remoteness.join(', ') : '';
  const levelStr = roleLevel.length ? roleLevel.join(', ') : '';
  const langsStr = languages.length ? languages.join(', ') : '';
  const indStr = industry.length ? industry.join(', ') : '';
  const compStr = company.length ? company.join(', ') : '';

  const segments: string[] = [];

  // Build the title segments based on locale-specific formatting rules.
  if (locale === 'en') {
    // Example: "Senior English Tech Jobs at Acme Inc in Helsinki (Remote)"
    const titleParts: string[] = [];
    if (levelStr) titleParts.push(levelStr);
    if (langsStr) titleParts.push(langsStr);
    if (indStr) titleParts.push(indStr);
    segments.push(titleParts.length ? `${titleParts.join(' ')} Jobs` : 'Jobs');
    if (compStr) segments.push(`at ${compStr}`);
    if (locStr) segments.push(`in ${locStr}`);
    if (remStr) segments.push(`(${remStr})`);
  } else if (locale === 'fi') {
    // Example: "Senior Englanti Tech Työt yrityksessä Acme Inc, Helsinki (Etä)"
    const titleParts: string[] = [];
    if (levelStr) titleParts.push(levelStr);
    if (langsStr) titleParts.push(langsStr);
    if (indStr) titleParts.push(indStr);
    segments.push(titleParts.length ? `${titleParts.join(' ')} Työt` : 'Työt');
    if (compStr) segments.push(`yrityksessä ${compStr}`);
    if (locStr) segments.push(locStr);
    if (remStr) segments.push(`(${remStr})`);
  } else if (locale === 'sv') {
    // Example: "Senior Engelska Tech Jobb på Acme Inc i Stockholm (Distans)"
    const titleParts: string[] = [];
    if (levelStr) titleParts.push(levelStr);
    if (langsStr) titleParts.push(langsStr);
    if (indStr) titleParts.push(indStr);
    segments.push(titleParts.length ? `${titleParts.join(' ')} Jobb` : 'Jobb');
    if (compStr) segments.push(`på ${compStr}`);
    if (locStr) segments.push(`i ${locStr}`);
    if (remStr) segments.push(`(${remStr})`);
  } else {
    // Fallback to English if locale is not recognized.
    const titleParts: string[] = [];
    if (levelStr) titleParts.push(levelStr);
    if (langsStr) titleParts.push(langsStr);
    if (indStr) titleParts.push(indStr);
    segments.push(titleParts.length ? `${titleParts.join(' ')} Jobs` : 'Jobs');
    if (compStr) segments.push(`at ${compStr}`);
    if (locStr) segments.push(`in ${locStr}`);
    if (remStr) segments.push(`(${remStr})`);
  }

  // Add site name for branding
  segments.push('JobCrawls.com');

  // Build title with clamping at 60 characters
  return buildClampedTitle(segments, ' ');
}

/**
 * The locale's compiled catalogue (unknown locales get English). In the browser only the locale
 * the client entry awaited is loaded; see i18n/locale-messages.ts.
 */
export function loadLocaleData(locale: string): Messages {
  return getLocaleMessages(locale);
}

export function reconstructURL(protocol: string, host: string, locale: string | undefined, path: string): string {
  return protocol + '//' + host + (locale ? `/${locale}` : '') + path;
}
